// Suggestion cards (docs/APP_REFERENCE.md "Suggestion cards"): early in a listen a small card above the dock offers
// one-tap weather (after WEATHER_AT of listening), later the city tour (after TOUR_AT, and at least TOUR_GAP after the
// weather card went). Each comes once per browser, never to one that has used that feature already, and a card nobody
// touches leaves after SHOW_MS. A holdout (HOLDOUT of browsers) never gets them, so their listening can be compared
// with the rest. The scheduling is pure (tests/nudge.test.mjs); createNudgeCard draws the card, in the look and place
// of the radio's vote card (site/js/ui/radio-vote.js).
export const NUDGES = ['weather', 'tour'];          // in the order they come
export const GROUPS = ['on', 'holdout'];
export const HOLDOUT = 0.2;
export const WEATHER_AT = 20000, TOUR_AT = 120000, TOUR_GAP = 60000, SHOW_MS = 12000;
export const TOUR_MINUTES = '5';                    // the tour the card starts (a Tour menu mode)
const STEP_MAX = 2000;                              // one step counts at most this: a throttled or sleeping tab isn't listening

/** This browser's group: ?nudges=on|holdout (a test), else the one it has, else a new draw. */
export function nudgeGroup(param, saved, random = Math.random) {
  if (GROUPS.includes(param)) return param;
  if (GROUPS.includes(saved)) return saved;
  return random() < HOLDOUT ? 'holdout' : 'on';
}

/** The weather card's three picks: rain, snow, leaves; a city that rains already (its own rain bed) gets the storm instead. */
export function weatherChoices(scene) {
  return [(scene?.ambience?.rain ?? 0) > 0 ? 'thunderstorm' : 'rain', 'snow', 'leaves'];
}

/** Whether a card can still come to this browser (the page stops asking once none can). */
export const nudgesLeft = ({ group, seen, used }) => group === 'on' && NUDGES.some(k => !seen.includes(k) && !used[k]);

/** A visit's clock, as it starts: nothing heard, no card, none shown. */
export const nudgeClock = () => ({ heard: 0, card: null, closedAt: null, had: [] });

/** The card went (picked or closed): the tour's gap counts from here. */
export const closeNudge = s => ({ ...s, card: null, closedAt: s.heard });

/**
 * One step of a visit's clock, about every second. s: { heard (ms listened), card: { kind, shown (ms on screen) } or
 * null, closedAt (heard as the last card went, or null), had (the kinds shown this visit) }. dt: ms since the last
 * step. group: 'on' | 'holdout' | null (none: OBS, ?creator); seen: the kinds this browser has had a card for (kept);
 * used: { weather, tour }, true once this browser picked a weather or started a tour, any way. playing: the music;
 * visible: the tab; open: a panel or dialog; blocked: the vote card shows or the interface is hidden (H); held: the card
 * is under the pointer or has focus.
 * Listening counts while the music plays in view with nothing open; a card comes only then, and waits while blocked.
 * Up, it stays through a pause in the music and counts its SHOW_MS only while it can be seen and isn't held.
 * Returns { state, show: the kind to show now or null, end: { kind, how: 'timeout' | 'elsewhere' } or null }.
 */
export function stepNudge(s, { dt, group, seen, used, playing, visible, open, blocked, held }) {
  const step = Math.min(Math.max(dt, 0), STEP_MAX), listening = playing && visible && !open;
  const heard = s.heard + (listening ? step : 0);
  if (s.card) {
    const { kind } = s.card, gone = how => ({ state: { ...s, heard, card: null, closedAt: heard }, show: null, end: { kind, how } });
    if (used[kind]) return gone('elsewhere');         // the menu, W or C got there first
    const shown = s.card.shown + (visible && !open && !blocked && !held ? step : 0);
    if (shown >= SHOW_MS) return gone('timeout');
    return { state: { ...s, heard, card: { kind, shown } }, show: null, end: null };
  }
  const due = { weather: heard >= WEATHER_AT, tour: heard >= TOUR_AT && (s.closedAt === null || heard - s.closedAt >= TOUR_GAP) };
  const kind = group === 'on' && listening && !blocked
    ? NUDGES.find(k => due[k] && !seen.includes(k) && !s.had.includes(k) && !used[k]) : undefined;
  if (!kind) return { state: { ...s, heard }, show: null, end: null };
  return { state: { ...s, heard, card: { kind, shown: 0 }, had: [...s.had, kind] }, show: kind, end: null };
}

const WEATHER_PICKS = { rain: ['Rain', '#i-rain'], thunderstorm: ['Storm', '#i-storm'], snow: ['Snow', '#i-snow'], leaves: ['Leaves', '#i-leaf'] };
const orList = words => `${words.slice(0, -1).join(', ')} or ${words.at(-1)}`;
const CARDS = {
  weather: {
    title: 'Try the weather', note: 'one tap for every city, sounds too · more under Weather',
    says: picks => `A suggestion above the player: try the weather, ${orList(picks.map(w => WEATHER_PICKS[w][0].toLowerCase()))}, on every city.`,
  },
  tour: {
    title: 'City tour', note: 'cities change on their own · Tour turns it off',
    says: () => `A suggestion above the player: take the city tour, a new city every ${TOUR_MINUTES} minutes.`,
  },
};

/**
 * The card in `el` (site/index.html #nudge), announced through `sr` (#nudgeSr, a status line that is always there):
 * show(kind, choices) fills and shows it, wait(on) keeps it out of sight a while (the vote card's turn), hide() takes it
 * away (true if it had focus). onPick(kind, choice) hears a choice, onClose(kind) the × or Escape. hover() says whether
 * :hover means a pointer is there (touch screens keep it on the last tap).
 */
export function createNudgeCard(el, sr, { onPick, onClose, hover }) {
  const title = el.querySelector('.nudge-title'), opts = el.querySelector('.rv-opts'), note = el.querySelector('.rv-note');
  let kind = null;
  el.querySelector('.nudge-close').addEventListener('click', () => { if (kind) onClose(kind); });
  el.addEventListener('keydown', e => {
    if (e.key !== 'Escape' || !kind) return;
    e.stopPropagation();                              // the page's own Escape closes panels, none of which is open
    onClose(kind);
  });
  const button = (choice, text, icon, meta = null) => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'rv-opt';
    b.innerHTML = '<svg aria-hidden="true"><use/></svg><span class="rv-name"></span>';
    b.querySelector('use').setAttribute('href', icon);
    b.querySelector('.rv-name').textContent = text;
    if (meta) b.append(Object.assign(document.createElement('b'), { className: 'rv-n', textContent: meta }));
    b.addEventListener('click', () => { if (kind) onPick(kind, choice); });
    return b;
  };
  return {
    show(k, choices) {
      kind = k;
      el.dataset.kind = k;
      title.textContent = CARDS[k].title;
      note.textContent = CARDS[k].note;
      opts.replaceChildren(...(k === 'weather'
        ? choices.map(w => button(w, ...WEATHER_PICKS[w]))
        : [button(TOUR_MINUTES, 'Take the tour', '#i-tour', `a new city every ${TOUR_MINUTES} min`)]));
      el.hidden = false;
      sr.textContent = CARDS[k].says(choices);
    },
    wait(on) { if (kind) el.hidden = on; },
    hide() {
      const hadFocus = el.contains(document.activeElement);
      kind = null;
      el.hidden = true;
      sr.textContent = '';
      return hadFocus;
    },
    get kind() { return kind; },
    get held() { return !!kind && (el.matches(':focus-within') || (hover() && el.matches(':hover'))); },
  };
}
